import React from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import { baseURL } from '../../api/baseURL';
import AdminShell from '../layout/AdminShell';
import Loader from '../../Loader';
import Modal from '../../Modal';
import { MDBBtn } from 'mdb-react-ui-kit';
import CallButton from '../CallButton';
import { isMainAdmin } from '../../utils/isMainAdmin';

type UnmatchedOrder = {
    receiptId: string;
    customerName: string | null;
    categoryName: string | null;
    address: string | null;
    venue: { lat: number; lg: number } | null;
    createdAt: string;
    eventTime: { from: string; till: string };
};

type Candidate = {
    id: string;
    name: string | null;
    contact: string | null;
    email: string | null;
    distanceKm: number;
    rating: number | null;
    specialization: string[];
};

type State = {
    orders: UnmatchedOrder[];
    candidates: Candidate[];
    selectedReceiptId: string | null;
    loadingOrders: boolean;
    loadingCandidates: boolean;
    requesting: boolean;
    requestedEmail: string | null;
    err: string | true | false;
    success: string | false;
    nowTick: number;
    candidateSearch: string;
};

/**
 * Availability page — routed at /availability. Phase 4 of the
 * admin-dashboard.html redesign: a dispatch/rescue tool for bookings that
 * are paid but still have no photographer. Ranks real candidates by
 * distance (no zone-chip browsing — see the Phase 4 plan's scope decision)
 * and reuses the exact same POST /request-order-confirmation flow already
 * proven on the Orders page (Admin/src/components/OrderList/OrderList.tsx's
 * requestPhotographer) — this sends a request to the photographer, it does
 * not instantly assign them (they still accept via the app).
 */
class AvailabilityPage extends React.Component<{ token?: string; role?: string }, State> {
    tickTimer: ReturnType<typeof setInterval> | null = null;

    state: State = {
        orders: [],
        candidates: [],
        selectedReceiptId: null,
        loadingOrders: false,
        loadingCandidates: false,
        requesting: false,
        requestedEmail: null,
        err: false,
        success: false,
        nowTick: Date.now(),
        candidateSearch: '',
    };

    componentDidMount(): void {
        this.fetchUnmatchedOrders();
        // Re-render every 30s so "searching for Xm" labels keep ticking
        // without needing to re-fetch the order list itself.
        this.tickTimer = setInterval(() => this.setState({ nowTick: Date.now() }), 30000);

        // Client-reported: Orders page's "Request Photographer"/"Reassign"
        // buttons now redirect here with ?receipt_id=... instead of showing
        // an email-entry modal — auto-select that order's candidates.
        const params = new URLSearchParams(window.location.search);
        const receiptId = params.get('receipt_id');
        if (receiptId) this.selectOrder(receiptId);
    }

    componentWillUnmount(): void {
        if (this.tickTimer) clearInterval(this.tickTimer);
    }

    fetchUnmatchedOrders = (): void => {
        const token = this.props.token;
        if (!token) return;
        this.setState({ loadingOrders: true });
        axios.get(`${baseURL}/unmatched-orders`, { headers: { Authorization: `Bearer ${token}` } })
            .then((response: any) => {
                this.setState({ orders: response?.data?.orders || [], loadingOrders: false });
            })
            .catch(() => {
                this.setState({ loadingOrders: false, err: true });
            });
    };

    selectOrder = (receiptId: string): void => {
        const token = this.props.token;
        this.setState({ selectedReceiptId: receiptId, candidates: [], loadingCandidates: true, candidateSearch: '' });
        if (!token) return;
        axios.get(`${baseURL}/order-candidates`, { headers: { Authorization: `Bearer ${token}` }, params: { receiptId } })
            .then((response: any) => {
                this.setState({ candidates: response?.data?.candidates || [], loadingCandidates: false });
            })
            .catch(() => {
                this.setState({ loadingCandidates: false, err: true });
            });
    };

    // Same call OrderList.tsx's requestPhotographer makes — reuses the
    // proven POST /request-order-confirmation flow as-is.
    requestPhotographer = (email: string, receiptId: string): void => {
        const token = this.props.token;
        if (!token || this.state.requesting || !email) return;
        this.setState({ requesting: true, err: false, requestedEmail: email });
        axios.post(`${baseURL}/request-order-confirmation`, { email: email.trim(), receipt_id: receiptId }, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((response: any) => {
                const msg = response?.data?.message;
                if (msg === 'request_sent') {
                    this.setState({ requesting: false, success: 'request_sent' });
                    this.fetchUnmatchedOrders();
                } else if (msg === 'request_already_sent') {
                    this.setState({ requesting: false, success: 'request_already_sent' });
                } else if (msg === 'email_failed') {
                    this.setState({ requesting: false, err: 'email_failed' });
                } else {
                    this.setState({ requesting: false, err: msg || true });
                }
            })
            .catch((error: any) => {
                const backendMsg = error?.response?.data?.message;
                this.setState({ requesting: false, err: backendMsg || true });
            });
    };

    toggleModal = (): void => {
        if (this.state.err) this.setState({ err: false });
        if (this.state.success) this.setState({ success: false });
    };

    elapsedLabel = (createdAt: string): string => {
        const minutes = Math.max(0, Math.round((this.state.nowTick - new Date(createdAt).getTime()) / 60000));
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        return `${hours}h ${minutes % 60}m`;
    };

    errorMessage = (): string => {
        const { err } = this.state;
        if (err === 'already_assigned') return 'Photographer is already assigned to an order at this time.';
        if (err === 'not_verified') return 'Photographer is not verified.';
        if (err === 'photographer_not_found') return "Photographer doesn't exist with that email.";
        if (err === 'order_not_found') return 'Order not found for this receipt id.';
        if (err === 'email_failed') return 'Photographer was assigned but the notification email failed to send. Please retry or contact the photographer directly.';
        if (err === 'failed_to_assign') return 'Failed to send the request. Please try again.';
        if (err === 'unauthorized' || err === 'auth_error') return 'You are not authorized to perform this action. Please log in again.';
        if (err === 'backend_unreachable') return 'Admin Server could not reach the main Backend. Please try again shortly.';
        if (err === '4_hours_restriction') return 'Cannot request a photographer: the shoot has already started or passed.';
        if (typeof err === 'string' && err) return err;
        return 'Something went wrong, please try again later.';
    };

    render() {
        const { orders, candidates, selectedReceiptId, loadingOrders, loadingCandidates, requesting, err, success, candidateSearch } = this.state;
        const selectedOrder = orders.find((o) => o.receiptId === selectedReceiptId) || null;
        const mainAdmin = isMainAdmin(this.props.role);
        const searchQuery = candidateSearch.trim().toLowerCase();
        const filteredCandidates = searchQuery
            ? candidates.filter((c) =>
                (c.name || '').toLowerCase().includes(searchQuery) ||
                (c.email || '').toLowerCase().includes(searchQuery)
            )
            : candidates;

        return (
            <AdminShell activePage="availability" title="Availability" subtitle="Rescue bookings that still have no photographer">
                <Modal
                    open={!!err || !!success}
                    hideSec
                    primaryText="Okay"
                    title="Update"
                    onConfirm={this.toggleModal}
                    toggleOpen={this.toggleModal}
                    content={<h6>{success === 'request_sent' ? 'Request sent to the photographer' : success === 'request_already_sent' ? 'A request has already been sent for this order' : this.errorMessage()}</h6>}
                />
                {(loadingOrders || requesting) ? <Loader /> : null}

                <div className="card" style={{ marginBottom: 16 }}>
                    <div className="card-body">
                        <h4 className="card-title">Unmatched bookings {orders.length ? `(${orders.length})` : ''}</h4>
                        {orders.length === 0 && !loadingOrders ? (
                            <p style={{ color: '#999', fontSize: 13 }}>No paid bookings are currently missing a photographer. 🎉</p>
                        ) : (
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                {orders.map((o) => {
                                    const selected = o.receiptId === selectedReceiptId;
                                    return (
                                        <div
                                            key={o.receiptId}
                                            onClick={() => this.selectOrder(o.receiptId)}
                                            style={{
                                                cursor: 'pointer', minWidth: 220, flex: '1 1 220px', maxWidth: 280,
                                                border: `1.5px solid ${selected ? '#0090e7' : 'rgba(255,255,255,0.12)'}`,
                                                background: selected ? 'rgba(0,144,231,0.1)' : 'rgba(255,255,255,0.03)',
                                                borderRadius: 12, padding: '12px 14px',
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                                <span style={{ fontWeight: 800, fontSize: 12.5 }}>#{o.receiptId.slice(-8)}</span>
                                                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#d9534f' }}>searching {this.elapsedLabel(o.createdAt)}</span>
                                            </div>
                                            <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 4 }}>{o.customerName || 'Customer'}</div>
                                            <div style={{ fontSize: 11, opacity: 0.65 }}>{o.categoryName}</div>
                                            <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2 }}>
                                                {o.address ? (o.address.length > 40 ? `${o.address.slice(0, 40)}…` : o.address) : '—'}
                                            </div>
                                            <div style={{ fontSize: 10.5, opacity: 0.5, marginTop: 4 }}>
                                                {o.eventTime?.from ? moment(new Date(o.eventTime.from)).format('MMM D, h:mm a') : ''}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                            <h4 className="card-title" style={{ marginBottom: selectedReceiptId && !loadingCandidates && candidates.length > 0 ? 0 : undefined }}>
                                {selectedReceiptId ? `Photographers for #${(selectedOrder?.receiptId || selectedReceiptId).slice(-8)}` : 'Photographers'}
                            </h4>
                            {selectedReceiptId && !loadingCandidates && candidates.length > 0 ? (
                                <input
                                    type="text"
                                    value={candidateSearch}
                                    onChange={(e) => this.setState({ candidateSearch: e.target.value })}
                                    placeholder="Search by name or email…"
                                    style={{
                                        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
                                        borderRadius: 8, padding: '7px 12px', fontSize: 13, color: '#fff', minWidth: 220,
                                    }}
                                />
                            ) : null}
                        </div>
                        {!selectedReceiptId ? (
                            <p style={{ color: '#999', fontSize: 13 }}>Select an unmatched booking above to see nearby available photographers.</p>
                        ) : loadingCandidates ? (
                            <p style={{ color: '#999', fontSize: 13 }}>Loading candidates…</p>
                        ) : candidates.length === 0 ? (
                            <p style={{ color: '#999', fontSize: 13 }}>No available, verified photographers match this booking's specialization/time right now.</p>
                        ) : filteredCandidates.length === 0 ? (
                            <p style={{ color: '#999', fontSize: 13, marginTop: 12 }}>No candidates match "{candidateSearch.trim()}".</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 12 }}>
                                {filteredCandidates.map((c) => (
                                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 2px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                        <div style={{
                                            width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0,
                                        }}>
                                            {(c.name || '?').slice(0, 1).toUpperCase()}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12.5, fontWeight: 800 }}>{c.name || 'Photographer'}</div>
                                            <div style={{ fontSize: 10.5, opacity: 0.6, fontWeight: 600 }}>
                                                {c.distanceKm} km away{c.rating != null ? ` · ★ ${c.rating}` : ''}
                                            </div>
                                        </div>
                                        <CallButton contact={c.contact} />
                                        {mainAdmin ? (
                                            <MDBBtn
                                                size="sm"
                                                disabled={!c.email || requesting}
                                                onClick={() => c.email && this.requestPhotographer(c.email, selectedOrder.receiptId)}
                                            >
                                                Request
                                            </MDBBtn>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </AdminShell>
        );
    }
}

const mapStateToProps = (state: any) => ({
    token: state.auth?.token,
    role: state.auth?.role,
});

export default connect(mapStateToProps)(AvailabilityPage);
